import React, { useEffect, useState } from 'react';
import {
  withLoading,
  useTranslator,
  withMobileAppMode,
  useAppContext,
  callNativeMobileApp,
  formatCalories,
} from '@front-activerse/common-components';
import { Container, Row, Col, Progress, Button, Modal } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';

import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';

import TrackerPlaceholder from './TrackerPlaceholder';
import TrackerTimeline from './components/TrackerTimeline';
import TrackerProgress from './components/TrackerProgress';
import TrackerActivityComponent from './components/TrackerActivityComponent';
import TrackerMealComponent from './components/TrackerMealComponent';
import TrackerNotificationAlert from './components/TrackerNotificationAlert';
import { includesGarminData } from './garminAttribution';

interface RemoteConfigFields {
  tracker_notification_id: number;
  tracker_notification_active: boolean;
}

interface ITrackerContainerProps {
  id: string;
  date: string;
  baseCalories: number;
  trainingCalories: number;
  totalCalories: number;
  consumedCalories: number;
  remainingCalories: number;
  macroSource: string;
  isGoalAchieved: boolean;
  isAvailable: boolean;
  macros: {
    protein: {
      targetGrams: number;
      targetPercentage: number;
      consumedGrams: number;
      remainingGrams: number;
    };
    fat: {
      targetGrams: number;
      targetPercentage: number;
      consumedGrams: number;
      remainingGrams: number;
    };
    carbs: {
      targetGrams: number;
      targetPercentage: number;
      consumedGrams: number;
      remainingGrams: number;
    };
  };
  events: any;
  removeMeal: (mealId: number) => Promise<any>;
  removeActivity: (activityId: number) => Promise<any>;
  unlinkActivity: (activityId: number) => Promise<any>;
  prefetchSurroundingDays: () => void;
  trackerArticleId: number;
  notificationData: RemoteConfigFields;
  closedNotifications: number[];
  userId: number;
  getClosedNotificationsValues: () => Promise<void>;
  markNotificationAsClosed: (
    collectionName: string,
    userId: number,
    notificationId: number
  ) => Promise<void>;
  loadingTrackerNotification: boolean;
  mobileAppMode: boolean;
  aiAccess: boolean;
  dietAccess: boolean;
}

const TrackerContainer: React.FC<ITrackerContainerProps> = ({
  date,
  baseCalories,
  trainingCalories,
  totalCalories,
  consumedCalories,
  remainingCalories,
  macros,
  events,
  removeMeal,
  removeActivity,
  unlinkActivity,
  prefetchSurroundingDays,
  isGoalAchieved,
  trackerArticleId,
  notificationData,
  closedNotifications,
  userId,
  getClosedNotificationsValues,
  markNotificationAsClosed,
  loadingTrackerNotification,
  mobileAppMode,
  aiAccess,
  dietAccess,
}) => {
  const { t } = useTranslator();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('aiAccess', aiAccess ? 'true' : 'false');
    sessionStorage.setItem('dietAccess', dietAccess ? 'true' : 'false');
  }, [aiAccess, dietAccess]);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const {
    images: {
      gearIcon,
      gearIconNeutral,
      addToTrackerIcon,
      describeMealIcon,
      scanFoodIcon,
      scanBarcodeIcon,
      scanLabelIcon,
      aiBadgeTiny,
      alertIcon,
      infoIcon,
      describeActivityIcon,
      createCustomMealIcon,
      mealFromAppIcon,
      yourMealsSavedIcon,
      trackerCookbook,
    },
  } = useAppContext();

  useEffect(() => {
    prefetchSurroundingDays();
  }, [date]);

  const round = (x: number) => {
    return Math.round(x * 10) / 10;
  };

  const percentageProteins = (macros?.protein?.consumedGrams / macros?.protein?.targetGrams) * 100;
  const percentageFats = (macros?.fat?.consumedGrams / macros?.fat?.targetGrams) * 100;
  const percentageCarbs = (macros?.carbs?.consumedGrams / macros?.carbs?.targetGrams) * 100;

  const reversedEvents = [...events].reverse();

  const showGarminAttribution = includesGarminData(trainingCalories, events);

  const renderTrackerNotificationAlert = () => {
    const userClosedNotification = closedNotifications.some(
      (notification) => notification === notificationData.tracker_notification_id
    );

    if (!userClosedNotification && notificationData.tracker_notification_active) {
      return (
        <Row>
          <Col md={{ size: 6, offset: 3 }}>
            <TrackerNotificationAlert
              userId={userId}
              trackerNotificationData={notificationData}
              getClosedNotificationsValues={getClosedNotificationsValues}
              markNotificationAsClosed={markNotificationAsClosed}
            />
          </Col>
        </Row>
      );
    }
    return null;
  };

  const handleAddToTracker = () => {
    if (mobileAppMode) {
      callNativeMobileApp({
        action: 'open_bottom_sheet',
        payload: {
          type: 'tracker_log',
        },
      });
    } else {
      setShowModal(true);
    }
  };

  const handleClick = (url: string) => {
    if (mobileAppMode) {
      callNativeMobileApp({
        action: 'open_modal',
        payload: {
          path: url,
          modal_variant: 'always_close_btn',
        },
      });
    } else {
      navigate(url, {
        state: {
          from: {
            pathname: url,
          },
        },
      });
    }
  };

  return (
    <>
      <Layout>
        <TitleBar showBackBtn={false}>
          {t('tracker.details.navbarTitle')}
          <div className="title-bar-icons">
            <Link to="/tracker/settings" className="ps-2">
              <img src={gearIcon} alt="" style={{ width: '32px', height: '32px' }} />
            </Link>
          </div>
        </TitleBar>

        <div className="page-template-tracker">
          <Container className="py-md-4">
            <Row>
              <Col xl={{ size: 10, offset: 1 }}>
                <div className="page-container">
                  <TrackerTimeline date={date} />

                  <div className="tracker-container">
                    <p className="small text-text-variant-color mb-m">
                      {baseCalories === 0 && <img src={alertIcon} alt="" className="me-1" />}
                      {t('tracker.dailyGoal.title')}{' '}
                      <span className="text-text-main-color">
                        {baseCalories > 0 ? formatCalories(baseCalories) : '--'} kcal
                      </span>
                      <Button
                        color="link"
                        className="p-0 ms-1 align-bottom"
                        onClick={() => handleClick('/diet-settings/goalSettings/weight')}
                      >
                        <img
                          src={gearIconNeutral}
                          style={{ width: '16px', height: '16px' }}
                          alt=""
                        />
                      </Button>
                    </p>

                    <Row className="align-items-center" noGutters>
                      <Col xs="3" className="text-center">
                        <p className="description text-text-main-color mb-0">
                          {consumedCalories > baseCalories && baseCalories > 0
                            ? t('tracker.overLimit.title')
                            : t('tracker.leftCalories.title')}
                        </p>
                        <h5 className="title-small semi-bold mb-0">
                          {baseCalories > 0 ? (
                            <span>
                              {consumedCalories > baseCalories
                                ? `+${formatCalories(consumedCalories - baseCalories)}`
                                : formatCalories(remainingCalories)}
                            </span>
                          ) : (
                            '--'
                          )}
                        </h5>
                        <p className="description text-text-variant-color mb-0">kcal</p>
                      </Col>

                      <Col xs="6">
                        <TrackerProgress
                          unit="kcal"
                          value={consumedCalories}
                          goal={baseCalories}
                          isGoalAchieved={isGoalAchieved}
                        />
                      </Col>

                      <Col xs="3" className="text-center">
                        <p className="description text-text-main-color mb-0">
                          {t('tracker.burnedCalories.title')}
                        </p>
                        <h5 className="title-small semi-bold mb-0">
                          {formatCalories(trainingCalories)}
                        </h5>
                        <p className="description text-text-variant-color mb-0">kcal</p>
                      </Col>
                    </Row>
                    <Row className="mt-s mb-xl">
                      <Col>
                        {baseCalories === 0 && (
                          <div className="tracker-missing-data-alert">
                            <p className="description text-text-main-color mb-0">
                              {t('tracker.missingData.setGoal.content')}
                            </p>
                            <Button
                              color="link"
                              size="sm"
                              tag={Link}
                              to="/diet-settings/all"
                              className="p-0 text-nowrap"
                            >
                              {t('tracker.missingData.setGoal.button')}
                            </Button>
                          </div>
                        )}

                        <Link
                          to="/tracker/settings/macro"
                          state={{
                            from: {
                              pathname: '/tracker/settings/macro',
                            },
                          }}
                        >
                          <div className="white-box position-relative">
                            {baseCalories > 0 ? (
                              <Row>
                                <Col>
                                  <p className="description text-text-variant-color mb-xxs">
                                    {t('tracker.marcosProtein.title')}
                                  </p>
                                  <Progress
                                    className="progress-proteins mb-xxs"
                                    value={percentageProteins}
                                  />
                                  <p className="mb-0 small text-text-main-color">
                                    {round(macros?.protein?.consumedGrams)} /{' '}
                                    {round(macros?.protein?.targetGrams)} g
                                  </p>
                                </Col>
                                <Col>
                                  <p className="description text-text-variant-color mb-xxs">
                                    {t('mealPlan.nutritionalInfo.fats.title')}
                                  </p>
                                  <Progress
                                    className="progress-fats mb-xxs"
                                    value={percentageFats}
                                  />
                                  <p className="mb-0 small text-text-main-color">
                                    {round(macros?.fat?.consumedGrams)} /{' '}
                                    {round(macros?.fat?.targetGrams)} g
                                  </p>
                                </Col>
                                <Col>
                                  <p className="description text-text-variant-color mb-xxs">
                                    {t('mealPlan.nutritionalInfo.carbs.title')}
                                  </p>
                                  <Progress
                                    className="progress-carbs mb-xxs"
                                    value={percentageCarbs}
                                  />
                                  <p className="mb-0 small text-text-main-color">
                                    {round(macros?.carbs?.consumedGrams)} /{' '}
                                    {round(macros?.carbs?.targetGrams)} g
                                  </p>
                                </Col>
                              </Row>
                            ) : (
                              <Row>
                                <Col>
                                  <p className="description text-text-variant-color mb-xxs">
                                    {t('tracker.marcosProtein.title')}
                                  </p>
                                  <p className="mb-0 small text-text-main-color">
                                    {round(macros?.protein?.consumedGrams)} g
                                  </p>
                                </Col>
                                <Col>
                                  <p className="description text-text-variant-color mb-xxs">
                                    {t('mealPlan.nutritionalInfo.fats.title')}
                                  </p>
                                  <p className="mb-0 small text-text-main-color">
                                    {round(macros?.fat?.consumedGrams)} g
                                  </p>
                                </Col>
                                <Col>
                                  <p className="description text-text-variant-color mb-xxs">
                                    {t('mealPlan.nutritionalInfo.carbs.title')}
                                  </p>
                                  <p className="mb-0 small text-text-main-color">
                                    {round(macros?.carbs?.consumedGrams)} g
                                  </p>
                                </Col>
                              </Row>
                            )}

                            <img
                              src={gearIcon}
                              alt=""
                              className="position-absolute"
                              style={{ width: '14px', height: '14px', top: '2px', right: '2px' }}
                            />
                          </div>
                        </Link>
                      </Col>
                    </Row>

                    <div className="text-center">
                      <Button
                        color="tracker-animated"
                        size="sm"
                        onClick={() => handleAddToTracker()}
                      >
                        <span>
                          <img
                            src={addToTrackerIcon}
                            alt={t('tracker.meal.addToTrackerButton')}
                            className="me-2"
                          />
                          {t('tracker.details.addButton')}
                        </span>
                      </Button>
                    </div>

                    {showGarminAttribution && (
                      <div
                        className="d-flex align-items-center gap-1 mt-l"
                        data-testid="tracker-garmin-attribution"
                      >
                        <img
                          src={infoIcon}
                          alt=""
                          style={{ width: '14px', height: '14px', flexShrink: 0 }}
                        />
                        <p className="description text-text-variant-color mb-0">
                          {t('wearables.dataAttribution.garmin')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* History */}
                <div className="tracker-history">
                  {/* Macro settings alert */}
                  {!loadingTrackerNotification && renderTrackerNotificationAlert()}

                  {reversedEvents.length > 0 ? (
                    <>
                      <h3 className="title-extra-small semi-bold mb-m">
                        {t('tracker.history.title')}
                      </h3>

                      <div className="tracker-history-list">
                        {reversedEvents.map((event: any) => {
                          if (event.__typename === 'SimpleDailyTrackerActivity') {
                            return (
                              <TrackerActivityComponent
                                {...event}
                                removeActivity={removeActivity}
                                unlinkActivity={unlinkActivity}
                                date={date}
                              />
                            );
                          } else if (event.__typename === 'DailyTrackerMealWithProducts') {
                            return (
                              <TrackerMealComponent
                                {...event}
                                removeMeal={removeMeal}
                                date={date}
                              />
                            );
                          }
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="no-history">
                      <h3 className="title-extra-small semi-bold mb-s">
                        {t('tracker.history.noHistory.title')}
                      </h3>

                      <p className="small text-text-variant-color mb-0">
                        {t('tracker.history.noHistory.description')}
                      </p>
                    </div>
                  )}

                  <hr />

                  <p className="description text-text-variant-color mb-s">
                    {t('tracker.sourceInfo.content')}
                    <br />
                  </p>
                  <p className="description">
                    <Link to={`/knowledge/${trackerArticleId}`}>
                      {t('tracker.sourceInfo.button')}
                    </Link>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </Layout>

      <Modal
        className="from-bottom"
        isOpen={showModal}
        fullscreen={true}
        size="lg"
        toggle={toggleModal}
      >
        <div className="select-insert-date">
          <p className="mb-m">{t('actionSheet.logToTracker.title')}</p>

          <Container style={{ maxHeight: '70dvh', overflowY: 'scroll' }}>
            <Row>
              <Col xs="12" md={{ size: 4, offset: 4 }}>
                <Button
                  color="tracker"
                  className="w-100 meal mt-0"
                  tag={Link}
                  to={`/tracker/scan-plate`}
                  onClick={toggleModal}
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div>
                      <img
                        src={scanFoodIcon}
                        alt={t('actionSheet.logToTracker.option.scan.food')}
                      />
                      {t('actionSheet.logToTracker.option.scan.food')}
                    </div>
                    <div className="text-end">
                      {!aiAccess && (
                        <span className="me-1">{t('actionSheet.label.getAccess')}</span>
                      )}
                      <img src={aiBadgeTiny} alt="" className="ai-badge" />
                    </div>
                  </div>
                </Button>

                <Button
                  color="tracker"
                  className="w-100 meal"
                  tag={Link}
                  to={`/tracker/describe-meal`}
                  onClick={toggleModal}
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div>
                      <img
                        src={describeMealIcon}
                        alt={t('actionSheet.logToTracker.option.describeMeal')}
                      />
                      {t('actionSheet.logToTracker.option.describeMeal')}
                    </div>
                    <div className="text-end">
                      {!aiAccess && (
                        <span className="me-1">{t('actionSheet.label.getAccess')}</span>
                      )}
                      <img src={aiBadgeTiny} alt="" className="ai-badge" />
                    </div>
                  </div>
                </Button>

                <Button
                  color="tracker"
                  className="w-100 meal"
                  tag={Link}
                  to={`/tracker/scan-barcode`}
                  onClick={toggleModal}
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div>
                      <img
                        src={scanBarcodeIcon}
                        alt={t('actionSheet.logToTracker.option.scan.barcode')}
                      />
                      {t('actionSheet.logToTracker.option.scan.barcode')}
                    </div>
                    <div className="text-end">
                      {!aiAccess && (
                        <span className="me-1">{t('actionSheet.label.getAccess')}</span>
                      )}
                      <img src={aiBadgeTiny} alt="" className="ai-badge" />
                    </div>
                  </div>
                </Button>

                <Button
                  color="tracker"
                  className="w-100 meal"
                  tag={Link}
                  to={`/tracker/scan-label`}
                  state={{ fromAutoScan: true }}
                  onClick={toggleModal}
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div>
                      <img
                        src={scanLabelIcon}
                        alt={t('actionSheet.logToTracker.option.scan.label')}
                      />
                      {t('actionSheet.logToTracker.option.scan.label')}
                    </div>
                    <div className="text-end">
                      {!aiAccess && (
                        <span className="me-1">{t('actionSheet.label.getAccess')}</span>
                      )}
                      <img src={aiBadgeTiny} alt="" className="ai-badge" />
                    </div>
                  </div>
                </Button>

                <Button
                  color="tracker"
                  tag={Link}
                  to="/tracker/describe-manual-meal"
                  className="w-100 meal"
                  onClick={toggleModal}
                >
                  <div>
                    <img
                      src={createCustomMealIcon}
                      alt={t('actionSheet.logToTracker.option.createCustomMeal')}
                    />
                    {t('actionSheet.logToTracker.option.createCustomMeal')}
                  </div>
                  <div className="text-end">
                    {/* <span className="mt-1 new">{t('actionSheet.label.new')}</span> */}
                  </div>
                </Button>

                <hr className="my-3" />

                <Button
                  color="tracker"
                  tag={Link}
                  to="/meals/user-meals"
                  className="w-100 meal2"
                  onClick={toggleModal}
                >
                  <div>
                    <img
                      src={yourMealsSavedIcon}
                      alt={t('actionSheet.logToTracker.option.yourMeals')}
                    />
                    {t('actionSheet.logToTracker.option.yourMeals')}
                  </div>
                </Button>

                <Button
                  color="tracker"
                  tag={Link}
                  to="/meals/cookbook"
                  className="w-100 meal2"
                  onClick={toggleModal}
                >
                  <div>
                    <img
                      src={trackerCookbook}
                      alt={t('app.actionSheet.logToTracker.option.mealFromApp.cookbook')}
                    />
                    {t('app.actionSheet.logToTracker.option.mealFromApp.cookbook')}
                  </div>
                </Button>

                <Button
                  color="tracker"
                  tag={Link}
                  to="/meals/day-plan"
                  className="w-100 meal2"
                  onClick={toggleModal}
                >
                  <div>
                    <img
                      src={mealFromAppIcon}
                      alt={t('actionSheet.logToTracker.option.mealFromApp.mealPlan')}
                    />
                    {t('actionSheet.logToTracker.option.mealFromApp.mealPlan')}
                  </div>
                </Button>

                <hr className="my-3" />

                <Button
                  color="tracker"
                  tag={Link}
                  to="/tracker/describe-activity"
                  className="w-100 activity"
                  onClick={toggleModal}
                >
                  <div>
                    <img
                      src={describeActivityIcon}
                      alt={t('actionSheet.logToTracker.option.describeActivity')}
                    />
                    {t('actionSheet.logToTracker.option.describeActivity')}
                  </div>
                  <div className="text-end">
                    {/* <span className="mt-1 new">{t('actionSheet.label.new')}</span> */}
                    {!aiAccess && <span className="me-1">{t('actionSheet.label.getAccess')}</span>}
                    <img src={aiBadgeTiny} alt="" className="ai-badge" />
                  </div>
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </Modal>
    </>
  );
};

const Placeholder = ({ date, loading }: { date: string; loading: boolean }) => (
  <TrackerPlaceholder date={date} loading={loading} />
);

export default withLoading(withMobileAppMode(TrackerContainer), Placeholder);
